import cx from 'classnames';
import {type FC, type MouseEventHandler, useCallback, useContext, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {FabricInvitationContext} from '~/common/FabricInvitation/context/FabricInvitationContext';
import {DesignErrorAlert} from '~/common/FabricInvitation/EventCreateDesign/components/DesignErrorAlert/DesignErrorAlert';
import {Sections} from '~/common/FabricInvitation/EventCreateDesign/constants';
import {EventCreateContext} from '~/common/FabricInvitation/EventCreateDesign/EventCreateContext';
import {UPDATE_DESIGN_OPTIONS} from '~/common/fabric/reducers/fabricDesign/constants';
import {selectCurrentSection} from '~/common/fabric/reducers/fabricDesign/selectors';
import type {FabricArt, FabricObject} from '~/common/fabric/types';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';

import './CanvasTooltip.sass';

interface CanvasTooltipProps {
  fabricArt: FabricArt;
  originalFabricArt: FabricArt;
  isStep4: boolean;
  inStep4Variant?: boolean;
}

export const CanvasTooltip: FC<CanvasTooltipProps> = ({
  fabricArt,
  originalFabricArt,
  isStep4,
  inStep4Variant = false,
}) => {
  const dispatch = useDispatch();
  const {canvas} = useContext(EventCreateContext);
  const {formValidations} = useContext(FabricInvitationContext);
  const isDesktop = useIsLargishAndUp();
  const currentSection: string = useSelector(selectCurrentSection);

  const [showCanvasTooltip, setShowCanvasTooltip] = useState(false);
  const [alertText, setAlertText] = useState('all');

  // TODO: Remove this if step 4 variant wins. This is only for control
  const handleTooltipClick = useCallback(() => {
    const canvasObjects = canvas?.getObjects();
    const defaultCanvasObject = canvasObjects
      .filter((canObj: FabricObject) => canObj?.type === 'textbox')
      .find((canObj: FabricObject) => canObj?._originalObject?.text === canObj?.text);

    canvas?.setActiveObject(defaultCanvasObject);
  }, [canvas]);

  const handleTooltipClose: MouseEventHandler = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowCanvasTooltip(false);
  }, []);

  useEffect(() => {
    dispatch({type: UPDATE_DESIGN_OPTIONS, payload: {showCanvasTooltip: showCanvasTooltip}});
  }, [dispatch, showCanvasTooltip]);

  // THIS USEEFFECT IS ONLY FOR STEP 4 CONTROL
  useEffect(() => {
    if (inStep4Variant) return;
    if (!isStep4) return setShowCanvasTooltip(false);

    const fabricArtTextboxes = fabricArt.objects.filter((obj) => obj.type === 'textbox');
    const originalTexts = new Set(
      originalFabricArt.objects.filter(({type}) => type === 'textbox').map(({text}) => text)
    );

    if (originalTexts.size <= 0) {
      return setShowCanvasTooltip(false);
    }

    const unchangedText = fabricArtTextboxes.every(({text}) => originalTexts.has(text));
    setShowCanvasTooltip(unchangedText);
  }, [inStep4Variant, isStep4, fabricArt, originalFabricArt]);

  // THIS USEEFFECT IS ONLY FOR STEP 4 VARIANT
  useEffect(() => {
    if (!inStep4Variant) return;
    if (!formValidations || !isStep4) return setShowCanvasTooltip(false);

    const designErrors = Object.entries(formValidations.current).reduce((acc, [key, validate]) => {
      if (!['designText', 'designUYO', 'designLogo'].includes(key)) return acc;
      const validation = validate();
      if (validation) acc.push(key);
      return acc;
    }, [] as string[]);
    if (designErrors.length === 0) return setShowCanvasTooltip(false);

    if (
      designErrors.includes('designText') &&
      ['designUYO', 'designLogo'].some((err) => designErrors.includes(err))
    ) {
      setAlertText('Please update all required fields.');
    } else if (designErrors.includes('designText')) {
      setAlertText('Please edit sample text to continue.');
    } else if (designErrors.includes('designUYO')) {
      setAlertText('Please upload a photo to continue.');
    } else if (designErrors.includes('designLogo')) {
      setAlertText('Please upload a logo to continue.');
    }
    return setShowCanvasTooltip(true);
  }, [formValidations, isStep4, inStep4Variant]);

  // CONTROL TOOLTIP
  if (!inStep4Variant) {
    return showCanvasTooltip && currentSection === Sections.UPLOAD_PHOTO ? (
      // biome-ignore lint/a11y/noStaticElementInteractions: fix accessibilty
      <div
        role="presentation"
        className={cx('tooltip-overlay', {
          'tooltip-overlay--desktop': isDesktop,
          'tooltip-visible': showCanvasTooltip,
          'tooltip__step-4-control': true,
        })}
        onClick={handleTooltipClick}
      >
        <AlertLineIcon ratio={isDesktop ? 1 : 0.75} color="red" />
        <Typography
          className={cx('tooltip-text', {'tooltip-text--desktop': isDesktop})}
          variant="paragraph3"
        >
          Edit sample text to finish invitation.
        </Typography>
      </div>
    ) : null;
  }

  // NEW TOOLTIP
  if (!isStep4 || !showCanvasTooltip) return null;
  if (![Sections.CARD_DESIGN, Sections.UPLOAD_PHOTO].includes(currentSection)) return null;
  return (
    <div
      role="presentation"
      className={cx('tooltip-overlay', {
        'tooltip-overlay--desktop': isDesktop,
        'tooltip-visible': showCanvasTooltip,
      })}
    >
      <DesignErrorAlert message={alertText} onClose={handleTooltipClose} />
    </div>
  );
};
